import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, X, MapPin, Clock, CalendarDays, MessageCircle, Phone, Mail, Tag, TrendingUp } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { TourMap } from "@/components/TourMap";
import { api, inr } from "@/lib/apiClient";
import { SITE, waLink, CATEGORY_LABELS } from "@/data/site";
import useSeo from "@/lib/useSeo";

export default function PackageDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setItem(null); setNotFound(false);
    api.get(`/itineraries/${slug}`).then((r) => { setItem(r.data); setActive(0); }).catch(() => setNotFound(true));
  }, [slug]);

  useSeo({ title: item ? item.title : "Journey", description: item ? item.summary : "" });

  if (notFound) return (
    <div className="pt-40 pb-32 text-center">
      <p className="font-display text-4xl text-[#2A1808]">Journey not found</p>
      <Link to="/packages" className="inline-block mt-6 text-[#F49E1A] font-semibold">← Back to all journeys</Link>
    </div>
  );
  if (!item) return <div className="pt-40 pb-32 text-center text-[#8A6B47]">Loading…</div>;

  const gallery = item.gallery && item.gallery.length ? item.gallery : [item.hero_image];
  const waMsg = `Hi Vacanza Go, I'm interested in the "${item.title}" (${item.nights}N/${item.days}D) journey. Please share details.`;

  return (
    <div className="bg-[#FFF7EC]">
      {/* Hero */}
      <div className="relative h-[70vh] min-h-[460px]">
        <img src={gallery[active]} alt={item.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(42,24,8,0.5) 0%, rgba(42,24,8,0.15) 40%, rgba(42,24,8,0.85) 100%)" }} />
        <div className="absolute inset-0 flex flex-col justify-end">
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-8 lg:px-12 pb-12">
            <Link to="/packages" className="inline-flex items-center gap-2 text-[#F9EFE1]/80 text-sm mb-6 hover:text-white" data-testid="back-to-packages">
              <ArrowLeft size={16} /> All journeys
            </Link>
            <p className="text-[#F49E1A] uppercase tracking-widest text-xs">{CATEGORY_LABELS[item.category] || item.category} · {item.region}</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium text-white mt-3 leading-[1.02]">{item.title}</h1>
            <p className="mt-4 font-display italic text-2xl text-[#F9EFE1]/90">{item.summary}</p>
          </div>
        </div>
      </div>

      {gallery.length > 1 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 -mt-8 relative z-10 flex gap-3 flex-wrap">
          {gallery.map((g, i) => (
            <button key={`${g}-${i}`} onClick={() => setActive(i)} className={`h-16 w-24 rounded-md overflow-hidden border-2 transition ${i === active ? "border-[#F49E1A]" : "border-transparent opacity-70"}`}>
              <img src={g} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Main */}
        <div className="lg:col-span-2 space-y-14">
          <Reveal>
            <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#553204] border-b border-[#553204]/10 pb-6">
              <span className="inline-flex items-center gap-2"><Clock size={16} className="text-[#F49E1A]" /> {item.nights} Nights / {item.days} Days</span>
              <span className="inline-flex items-center gap-2"><CalendarDays size={16} className="text-[#F49E1A]" /> Best: {item.season || "Year-round"}</span>
              <span className="inline-flex items-center gap-2 capitalize"><MapPin size={16} className="text-[#F49E1A]" /> {item.tour_type}</span>
              {item.show_popularity && item.popularity > 0 && (
                <span className="inline-flex items-center gap-2 text-[#B4740C] font-semibold" data-testid="detail-popularity"><TrendingUp size={16} /> Popularity {item.popularity}/10</span>
              )}
            </div>
            <p className="mt-6 text-lg leading-relaxed text-[#553204]">{item.description}</p>
          </Reveal>

          {item.day_plan && item.day_plan.length > 0 && (
            <Reveal>
              <h2 className="font-display text-3xl font-medium text-[#2A1808] mb-6">Day by day</h2>
              <ol className="relative border-l-2 border-[#F49E1A]/30 ml-2">
                {item.day_plan.map((d) => (
                  <li key={d.day} className="mb-8 ml-6" data-testid={`day-plan-${d.day}`}>
                    <span className="absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full bg-[#F49E1A] text-white text-xs font-bold">{d.day}</span>
                    <h3 className="font-display text-xl text-[#2A1808]">{d.title}</h3>
                    <p className="mt-1 text-sm text-[#8A6B47] leading-relaxed">{d.detail}</p>
                  </li>
                ))}
              </ol>
            </Reveal>
          )}

          {item.map_points && item.map_points.length > 0 && (
            <Reveal>
              <h2 className="font-display text-3xl font-medium text-[#2A1808] mb-2">On the map</h2>
              <p className="text-sm text-[#8A6B47] mb-5">The route across your itinerary.</p>
              <TourMap points={item.map_points} />
            </Reveal>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {item.inclusions?.length > 0 && (
              <Reveal>
                <h3 className="font-display text-2xl text-[#2A1808] mb-4">What's included</h3>
                <ul className="space-y-2.5">
                  {item.inclusions.map((x, i) => (
                    <li key={`${x}-${i}`} className="flex items-start gap-2.5 text-sm text-[#553204]"><Check size={16} className="mt-0.5 shrink-0 text-[#25D366]" /> {x}</li>
                  ))}
                </ul>
              </Reveal>
            )}
            {item.exclusions?.length > 0 && (
              <Reveal delay={80}>
                <h3 className="font-display text-2xl text-[#2A1808] mb-4">Not included</h3>
                <ul className="space-y-2.5">
                  {item.exclusions.map((x, i) => (
                    <li key={`${x}-${i}`} className="flex items-start gap-2.5 text-sm text-[#8A6B47]"><X size={16} className="mt-0.5 shrink-0 text-[#553204]/40" /> {x}</li>
                  ))}
                </ul>
              </Reveal>
            )}
          </div>

          {item.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {item.tags.map((t) => (
                <Link key={t} to={`/packages?q=${encodeURIComponent(t)}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#F9EFE1] border border-[#553204]/10 px-3 py-1.5 text-xs text-[#553204] capitalize hover:border-[#F49E1A]">
                  <Tag size={11} /> {t}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Sticky booking card */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-28 rounded-md border border-[#553204]/12 bg-[#FFFBF5] p-7 shadow-[0_20px_50px_-30px_rgba(42,24,8,0.5)]">
            <p className="text-xs text-[#8A6B47]">Curated from</p>
            <p className="font-display text-4xl font-semibold text-[#2A1808]">{inr(item.price_from)} <span className="text-sm font-sans text-[#8A6B47] font-normal">/ person</span></p>
            <p className="mt-2 text-xs text-[#8A6B47]">Final quote is itemised & tailored to your group and dates.</p>

            <a href={waLink(waMsg)} target="_blank" rel="noreferrer" data-testid="detail-whatsapp-cta" className="mt-6 flex items-center justify-center gap-2 rounded-full btn-orange px-5 py-3.5 text-sm w-full">
              <MessageCircle size={17} /> Enquire on WhatsApp
            </a>
            <a href={`tel:${SITE.phoneRaw}`} data-testid="detail-call-cta" className="mt-3 flex items-center justify-center gap-2 rounded-full border border-[#553204]/20 px-5 py-3 text-sm w-full text-[#2A1808] hover:bg-[#553204]/5 transition-colors">
              <Phone size={16} /> {SITE.phone}
            </a>
            <a href={`mailto:${SITE.email}?subject=${encodeURIComponent("Enquiry: " + item.title)}`} data-testid="detail-email-cta" className="mt-3 flex items-center justify-center gap-2 rounded-full border border-[#553204]/20 px-5 py-3 text-sm w-full text-[#2A1808] hover:bg-[#553204]/5 transition-colors">
              <Mail size={16} /> Email an enquiry
            </a>
            <Link to="/recommender" className="mt-5 block text-center text-sm text-[#F49E1A] font-semibold">Not quite right? Plan a custom trip →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
